import { isEqual } from 'lodash';
import React, {
  useEffect,
  useRef,
  useState,
  useReducer,
  useCallback
} from 'react';
import useTranslations from '../../../hooks/useTranslations';
import generateId from '../../../utils/generateId';
import StatusQueryBuilderView from '../view/StatusQueryBuilderView';
import { EMPTY_BASE_QUERY } from './ConditionsQueryBuilderEngine';

// [{"field":"'statusData'->>'status'", "op": "=", "value": "8552418450438208"}]

const StatusQueryBuilderReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialState = {
  internOp: 'or',
  statuses: {}
};

const StatusQueryBuilderEngine = ({
  query = [],
  update = () => {},
  form = {},
  sentencePrefix
}) => {
  const [data, dispatch] = useReducer(StatusQueryBuilderReducer, initialState);
  const [inDeleteSequence, setInDeleteSequence] = useState(null);
  const [hoverOn, setHoverOn] = useState(null);

  const t = useTranslations();
  const skipUpdate = useRef(true);

  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    const query2Export = Object.values(data.statuses).map(s => ({
      field: "'statusData'->>'status'",
      op: '=',
      value: s,
      internOp: data.internOp
    }));
    update(query2Export);
  }, [data]);

  useEffect(() => {
    if (query.length) {
      skipUpdate.current = true;
      const statuses = {};

      query.forEach(qBlock => {
        statuses[`id-${generateId()}`] = qBlock.value;
      });

      dispatch({
        type: 'update',
        payload: { statuses }
      });
    }
  }, []);

  const setStatus = useCallback(
    (id, value) => {
      const statusesCpy = JSON.parse(JSON.stringify(data.statuses));
      statusesCpy[id] = value;
      dispatch({ type: 'update', payload: { statuses: statusesCpy } });
    },
    [data]
  );

  const removeCondition = useCallback(
    id => {
      const statusesCpy = JSON.parse(JSON.stringify(data.statuses));
      delete statusesCpy[id];
      dispatch({ type: 'update', payload: { statuses: statusesCpy } });
    },
    [data]
  );

  const onRemoveConditionClicked = useCallback(
    id => {
      if (Object.keys(data.statuses).length === 1) {
        // we're deleting the last (sub-)condition - we want to use the
        // condition delete and not sub-condition delete.
        // We trigger it by sending empt query
        update([]);
        return;
      }
      setInDeleteSequence(id);
    },
    [data, update]
  );

  const addCondition = useCallback(() => {
    const statusesCpy = JSON.parse(JSON.stringify(data.statuses));
    statusesCpy[`id-${generateId()}`] = null;
    dispatch({ type: 'update', payload: { statuses: statusesCpy } });
  }, [data]);

  const viewProps = {
    sentencePrefix: sentencePrefix || t('logic.when_statusd'),
    ...data,
    setStatus,
    options:
      (form.customStatuses &&
        form.customStatuses.map(s => ({
          text: s.label,
          value: s.internalId,
          'data-testid': `condition-builder-status-${s.internalId}`
        }))) ||
      [],
    inDeleteSequence,
    onRemoveConditionClicked,
    hoverOn,
    setHoverOn,
    addCondition,
    removeCondition
  };

  return <StatusQueryBuilderView {...viewProps} />;
};

export default StatusQueryBuilderEngine;
